import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Eye, EyeOff, Flag, Inbox, MailPlus, MessageCircle, RefreshCw, Search, Star, Trash2, X } from 'lucide-react';
import { deleteMessage, getMessages, getMessageStats, updateMessage } from '../../../data/api';
import { toast } from 'sonner';

const formatDate = (value) => {
  if (!value) return 'No timestamp';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No timestamp';
  return date.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const getPreview = (message) => message.content || message.message || message.comment || 'No preview available.';

const getMessageId = (message) => String(message?.id || message?._id || '');

function findGuestForMessage(message, guests) {
  const email = String(message?.email || '').trim().toLowerCase();
  if (!email) return null;
  return guests.find(guest => String(guest?.email || '').trim().toLowerCase() === email) || null;
}

function getInitials(message) {
  const name = message.guest_name || message.name || message.email || 'Guest';
  return String(name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('') || 'G';
}

export default function InboxPanel({ messages = [], unreadCount = 0, guests = [], onMessagesChanged, onPrepareReply }) {
  const [inboxMessages, setInboxMessages] = useState(messages);
  const [stats, setStats] = useState({ total: messages.length, unread: unreadCount });
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMessageId, setSelectedMessageId] = useState('');
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [replyTarget, setReplyTarget] = useState(null);
  const [error, setError] = useState('');

  const loadMessages = async () => {
    setLoading(true);
    setError('');
    try {
      const statusFilter = filter === 'all' ? null : filter;
      const [nextMessages, nextStats] = await Promise.all([
        getMessages(statusFilter),
        getMessageStats(),
      ]);
      setInboxMessages(Array.isArray(nextMessages) ? nextMessages : []);
      setStats(nextStats || {});
    } catch (err) {
      console.error(err);
      setError('Messages could not be loaded. Legacy Web Comments is still available while the Hub is in preview.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
    // Re-fetch when the backend filter changes. Search remains client-side.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    setInboxMessages(messages);
    setStats(prev => ({
      ...prev,
      total: prev.total ?? messages.length,
      unread: prev.unread ?? unreadCount,
    }));
  }, [messages, unreadCount]);

  const refreshAfterChange = async () => {
    await loadMessages();
    await onMessagesChanged?.();
  };

  const toggleStatus = async (message) => {
    setBusyId(message.id);
    try {
      const id = getMessageId(message);
      await updateMessage(id, { status: message.status === 'unread' ? 'read' : 'unread' });
      await refreshAfterChange();
    } catch (err) {
      console.error(err);
      toast.error('Message status could not be updated');
    } finally {
      setBusyId(null);
    }
  };

  const toggleFlag = async (message) => {
    setBusyId(message.id);
    try {
      const id = getMessageId(message);
      await updateMessage(id, { flagged: !message.flagged });
      await refreshAfterChange();
    } catch (err) {
      console.error(err);
      toast.error('Message flag could not be updated');
    } finally {
      setBusyId(null);
    }
  };

  const toggleHighlight = async (message) => {
    setBusyId(message.id);
    try {
      const id = getMessageId(message);
      await updateMessage(id, { highlighted: !message.highlighted });
      await refreshAfterChange();
    } catch (err) {
      console.error(err);
      toast.error('Message highlight could not be updated');
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const id = getMessageId(deleteTarget);
    setBusyId(id);
    try {
      await deleteMessage(id);
      setDeleteTarget(null);
      await refreshAfterChange();
      toast.success('Message deleted');
    } catch (err) {
      console.error(err);
      toast.error('Message could not be deleted');
    } finally {
      setBusyId(null);
    }
  };

  const filteredMessages = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return inboxMessages;
    return inboxMessages.filter(message => {
      const haystack = [
        message.guest_name,
        message.email,
        message.message_type,
        getPreview(message),
      ].join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [inboxMessages, searchTerm]);

  const selectedMessage = useMemo(() => {
    if (!filteredMessages.length) return null;
    return filteredMessages.find(message => getMessageId(message) === selectedMessageId) || filteredMessages[0];
  }, [filteredMessages, selectedMessageId]);

  const replyGuest = useMemo(
    () => findGuestForMessage(replyTarget, guests),
    [guests, replyTarget]
  );

  useEffect(() => {
    if (!filteredMessages.length) {
      setSelectedMessageId('');
      return;
    }
    const stillVisible = filteredMessages.some(message => getMessageId(message) === selectedMessageId);
    if (!stillVisible) {
      setSelectedMessageId(getMessageId(filteredMessages[0]));
    }
  }, [filteredMessages, selectedMessageId]);

  return (
    <div className="communications-panel communications-inbox" data-testid="communications-inbox-panel">
      <div className="communications-panel__toolbar">
        <div>
          <h2>Inbox</h2>
          <p>Guest messages from the existing Web Comments message system.</p>
        </div>
        <div className="communications-search">
          <Search size={16} />
          <input
            type="search"
            value={searchTerm}
            onChange={event => setSearchTerm(event.target.value)}
            placeholder="Search guest, email, type, or message"
            data-testid="communications-inbox-search"
          />
        </div>
      </div>

      <div className="communications-filter-row communications-filter-row--inbox">
        {[
          ['all', stats.total || 0],
          ['unread', stats.unread || 0],
          ['read', Math.max((stats.total || 0) - (stats.unread || 0), 0)],
        ].map(([key, count]) => (
          <button
            key={key}
            type="button"
            className={`communications-filter-button ${filter === key ? 'communications-filter-button--active' : ''}`}
            onClick={() => setFilter(key)}
            data-testid={`communications-inbox-filter-${key}`}
          >
            {key} <strong>{count}</strong>
          </button>
        ))}
        <button
          type="button"
          className="communications-icon-button communications-icon-button--refresh"
          onClick={loadMessages}
          disabled={loading}
          title="Refresh messages"
          data-testid="communications-inbox-refresh"
        >
          <RefreshCw size={16} />
        </button>
      </div>

      <div className="communications-kpi-row communications-inbox-kpis">
        <div className="communications-kpi">
          <span>Total messages</span>
          <strong>{stats.total || inboxMessages.length}</strong>
        </div>
        <div className="communications-kpi">
          <span>Unread</span>
          <strong>{stats.unread || 0}</strong>
        </div>
        <div className="communications-kpi">
          <span>Visible</span>
          <strong>{filteredMessages.length}</strong>
        </div>
      </div>

      {error && <div className="communications-error" data-testid="communications-inbox-error">{error}</div>}

      <div className="communications-inbox-triage">
        <div className="communications-inbox-list" aria-label="Guest message list">
          {filteredMessages.length === 0 ? (
          <div className="communications-empty">
            <Inbox size={24} />
            <h3>No guest messages to show</h3>
            <p>{loading ? 'Loading messages...' : 'Try another filter or search.'}</p>
          </div>
        ) : (
          filteredMessages.map(message => (
            <article
              key={getMessageId(message)}
              className={`communications-message-row ${message.status === 'unread' ? 'is-unread' : ''} ${getMessageId(selectedMessage) === getMessageId(message) ? 'is-selected' : ''}`}
              onClick={() => setSelectedMessageId(getMessageId(message))}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setSelectedMessageId(getMessageId(message));
                }
              }}
              role="button"
              tabIndex={0}
              aria-pressed={getMessageId(selectedMessage) === getMessageId(message)}
            >
              <div className={`communications-message-row__status ${message.status === 'unread' ? 'is-unread' : ''}`} aria-hidden="true" />
              <div className="communications-message-row__avatar" aria-hidden="true">
                {getInitials(message)}
              </div>
              <div className="communications-message-row__body">
                <div className="communications-message-row__meta">
                  <strong>{message.guest_name || message.name || 'Guest message'}</strong>
                  <span>{formatDate(message.created_at)}</span>
                </div>
                <div className="communications-message-row__submeta">
                  {message.email && <span>{message.email}</span>}
                  <span>{message.message_type || 'note'}</span>
                  {message.flagged && <span className="communications-pill communications-pill--warn">Flagged</span>}
                  {message.highlighted && <span className="communications-pill">Highlighted</span>}
                </div>
                <p>{getPreview(message)}</p>
              </div>
              <div className="communications-message-row__actions">
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleStatus(message);
                  }}
                  disabled={busyId === getMessageId(message)}
                  title={message.status === 'unread' ? 'Mark read' : 'Mark unread'}
                  aria-label={message.status === 'unread' ? 'Mark read' : 'Mark unread'}
                  data-testid={`communications-message-toggle-status-${getMessageId(message)}`}
                >
                  {message.status === 'unread' ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleFlag(message);
                  }}
                  disabled={busyId === getMessageId(message)}
                  className={message.flagged ? 'is-active-warn' : ''}
                  title={message.flagged ? 'Remove flag' : 'Flag message'}
                  aria-label={message.flagged ? 'Remove flag' : 'Flag message'}
                  data-testid={`communications-message-toggle-flag-${getMessageId(message)}`}
                >
                  <Flag size={16} />
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleHighlight(message);
                  }}
                  disabled={busyId === getMessageId(message)}
                  className={message.highlighted ? 'is-active' : ''}
                  title={message.highlighted ? 'Remove highlight' : 'Highlight message'}
                  aria-label={message.highlighted ? 'Remove highlight' : 'Highlight message'}
                  data-testid={`communications-message-toggle-highlight-${getMessageId(message)}`}
                >
                  <Star size={16} />
                </button>
              </div>
            </article>
          ))
        )}
        </div>

        <aside className="communications-inbox-detail" aria-label="Selected message detail">
          {selectedMessage ? (
            <>
              <div className="communications-inbox-detail__identity">
                <span className="communications-inbox-detail__avatar">{getInitials(selectedMessage)}</span>
                <div>
                  <p className="communications-panel__eyebrow">Guest Message</p>
                  <h3>{selectedMessage.guest_name || selectedMessage.name || 'Guest message'}</h3>
                  <span>{selectedMessage.email || 'No email on message'} · {formatDate(selectedMessage.created_at)}</span>
                </div>
              </div>

              <div className="communications-inbox-detail__badges">
                <span className={`communications-pill ${selectedMessage.status === 'unread' ? 'communications-pill--warn' : 'communications-pill--success'}`}>
                  {selectedMessage.status === 'unread' ? 'Unread' : 'Read'}
                </span>
                <span className="communications-pill">{selectedMessage.message_type || 'note'}</span>
                {selectedMessage.flagged && <span className="communications-pill communications-pill--warn">Flagged</span>}
                {selectedMessage.highlighted && <span className="communications-pill">Highlighted</span>}
              </div>

              <div className="communications-inbox-detail__message">
                <MessageCircle size={20} />
                <p>{getPreview(selectedMessage)}</p>
              </div>

              <div className="communications-inbox-detail__actions">
                <button type="button" className="communications-button communications-button--primary" onClick={() => setReplyTarget(selectedMessage)}>
                  <MailPlus size={16} />
                  Prepare Reply
                </button>
                <button type="button" className="communications-button communications-button--ghost" onClick={() => toggleStatus(selectedMessage)} disabled={busyId === getMessageId(selectedMessage)}>
                  {selectedMessage.status === 'unread' ? <Eye size={16} /> : <EyeOff size={16} />}
                  {selectedMessage.status === 'unread' ? 'Mark Read' : 'Mark Unread'}
                </button>
                <button type="button" className="communications-button communications-button--ghost" onClick={() => toggleFlag(selectedMessage)} disabled={busyId === getMessageId(selectedMessage)}>
                  <Flag size={16} />
                  {selectedMessage.flagged ? 'Remove Flag' : 'Flag'}
                </button>
                <button type="button" className="communications-button communications-button--ghost" onClick={() => toggleHighlight(selectedMessage)} disabled={busyId === getMessageId(selectedMessage)}>
                  <Star size={16} />
                  {selectedMessage.highlighted ? 'Remove Highlight' : 'Highlight'}
                </button>
              </div>

              <div className="communications-inbox-detail__danger">
                <button
                  type="button"
                  className="communications-button communications-button--danger"
                  onClick={() => setDeleteTarget(selectedMessage)}
                  disabled={busyId === getMessageId(selectedMessage)}
                  data-testid={`communications-message-delete-${getMessageId(selectedMessage)}`}
                >
                  <Trash2 size={16} />
                  Delete Message
                </button>
              </div>
            </>
          ) : (
            <div className="communications-empty">
              <Inbox size={24} />
              <p>Select a guest message to review.</p>
            </div>
          )}
        </aside>
      </div>

      {deleteTarget && (
        <div className="communications-confirm-backdrop" role="dialog" aria-modal="true" aria-labelledby="delete-message-title">
          <div className="communications-confirm">
            <button
              type="button"
              className="communications-confirm__close"
              onClick={() => setDeleteTarget(null)}
              aria-label="Cancel delete"
              data-testid="communications-delete-cancel-x"
            >
              <X size={16} />
            </button>
            <Trash2 size={24} />
            <h3 id="delete-message-title">Delete this message?</h3>
            <p>This uses the existing Web Comments delete behavior and cannot be undone.</p>
            <div className="communications-confirm__preview">{getPreview(deleteTarget)}</div>
            <div className="communications-confirm__actions">
              <button
                type="button"
                className="communications-button communications-button--ghost"
                onClick={() => setDeleteTarget(null)}
                data-testid="communications-delete-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                className="communications-button communications-button--danger"
                onClick={confirmDelete}
                disabled={busyId === deleteTarget.id}
                data-testid="communications-delete-confirm"
              >
                Delete Message
              </button>
            </div>
          </div>
        </div>
      )}

      {replyTarget && (
        <div className="communications-confirm-backdrop" role="dialog" aria-modal="true" aria-labelledby="prepare-reply-title">
          <div className="communications-confirm communications-live-confirm">
            <button
              type="button"
              className="communications-confirm__close"
              onClick={() => setReplyTarget(null)}
              aria-label="Cancel prepare reply"
            >
              <X size={16} />
            </button>
            <MailPlus size={24} />
            <h3 id="prepare-reply-title">Prepare Reply?</h3>
            <p>This opens Communications Send with guest context. It does not send anything until the message is previewed and submitted through safety controls.</p>
            <div className="communications-live-confirm__details">
              <div className="communications-send-summary__row">
                <span>Message from</span>
                <strong>{replyTarget.guest_name || replyTarget.name || 'Guest message'}</strong>
              </div>
              <div className="communications-send-summary__row">
                <span>Email on message</span>
                <strong>{replyTarget.email || 'No email'}</strong>
              </div>
              <div className="communications-send-summary__row">
                <span>Guest match</span>
                <strong>{replyGuest ? `${replyGuest.first_name || ''} ${replyGuest.last_name || ''}`.trim() || replyGuest.email : 'Choose recipient in Send'}</strong>
              </div>
            </div>
            {!replyGuest && (
              <div className="communications-send-warning communications-send-warning--compact">
                <AlertCircle size={18} />
                <span>No matching guest record was found by email. Confirm the recipient manually in Send before previewing.</span>
              </div>
            )}
            <div className="communications-confirm__actions">
              <button type="button" className="communications-button communications-button--ghost" onClick={() => setReplyTarget(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="communications-button communications-button--primary"
                onClick={() => {
                  const target = replyTarget;
                  setReplyTarget(null);
                  onPrepareReply?.(target);
                }}
              >
                Open Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
